import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'
import {
  Box,
  DatePicker,
  MultiSelectInput,
  Radio,
  RadioGroup,
} from '@hivewatch/dark'

import {
  DateValueI,
  FilterOptionI,
  isCustomDateRange,
} from '@/utils/filterDrawers'

import { getCustomDates, getEndOfDay, getStartOfDay } from '../utils/utils'
import { FilterRow } from './FilterRow'

interface CreatedAtFilterRowIProps {
  label: string
  placeholder: string
  colorScheme: ColorScheme
  options: FilterOptionI<number | DateValueI>[]
  value: FilterOptionI<number | DateValueI> | null | undefined
  onChange: (value: FilterOptionI<number | DateValueI> | null) => void
}

/**
 * Created At Filter Row: preset intervals as radios, with the Custom preset
 * revealing Start/End DatePickers. Selection renders as one dismissible chip
 * labelled with the preset (or "Custom").
 */
export const CreatedAtFilterRow = ({
  label,
  placeholder,
  colorScheme,
  options,
  value,
  onChange,
}: CreatedAtFilterRowIProps) => {
  const { t } = useTranslation()
  const [startDate, setStartDate] = useState(
    () => getCustomDates(value).startDate
  )
  const [endDate, setEndDate] = useState(() => getCustomDates(value).endDate)

  const isCustomSelected = value != null && isCustomDateRange(value)

  const chipOptions = options.map((option) => ({
    value: option.label,
    label: option.label,
  }))

  const emitCustomRange = (start: Date, end: Date) => {
    const customOption = options.find((option) => isCustomDateRange(option))
    if (!customOption) return
    onChange({
      label: customOption.label,
      value: {
        gte: getStartOfDay(start),
        lte: getEndOfDay(end),
      },
    })
  }

  const handleRadioChange = (selectedLabel: string) => {
    const option = options.find((o) => o.label === selectedLabel)
    if (!option) return
    if (isCustomDateRange(option)) {
      emitCustomRange(startDate, endDate)
    } else {
      onChange(option)
    }
  }

  const handleStartDateChange = (date: Date) => {
    setStartDate(date)
    emitCustomRange(date, endDate)
  }

  const handleEndDateChange = (date: Date) => {
    setEndDate(date)
    emitCustomRange(startDate, date)
  }

  return (
    <FilterRow colorScheme={colorScheme} label={label}>
      <MultiSelectInput
        colorScheme={colorScheme}
        onClear={() => onChange(null)}
        onRemove={() => onChange(null)}
        options={chipOptions}
        panelContent={
          <Box display='flex' flexDirection='column' gap='12px' p='12px'>
            <RadioGroup
              aria-label={label}
              colorScheme={colorScheme}
              name='createdAtRange'
              onChange={handleRadioChange}
              value={value?.label ?? null}
            >
              {options.map((option) => (
                <Radio
                  key={option.label}
                  label={option.label}
                  value={option.label}
                />
              ))}
            </RadioGroup>
            {isCustomSelected && (
              <>
                <DatePicker
                  colorScheme={colorScheme}
                  date={startDate}
                  label={t('common:start_date')}
                  maxDate={endDate}
                  onChange={handleStartDateChange}
                />
                <DatePicker
                  colorScheme={colorScheme}
                  date={endDate}
                  label={t('common:end_date')}
                  maxDate={new Date()}
                  minDate={startDate}
                  onChange={handleEndDateChange}
                />
              </>
            )}
          </Box>
        }
        placeholder={placeholder}
        selected={value ? [value.label] : []}
      />
    </FilterRow>
  )
}
